import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { codigoDoPedido } from "@/lib/codigo-do-pedido";
import { entregaDoPedido } from "@/components/prosthesis/endereco-de-entrega";
import type { ProsthesisServiceRow } from "@/components/prosthesis/types";
import type { IncomingOrderRow } from "./production-types";
import { ETIQUETAS_PADRAO } from "@bookdental/schemas";
import { imprimirPagina, paginaDeEntrega, paginaDeProducao } from "./etiquetas";
import { useLinkDoCatalogo } from "@/components/catalogo/publico/use-link-do-catalogo";
import { abrirImpressao } from "./impressao";
import { POPUP, paginaUnicaDoPedido, paraEtiqueta, pedidoDaEtiqueta } from "./etiqueta-do-pedido";
import { segurarDocumentoNaEstacao } from "@/lib/estacao-de-etiquetas";
import { CSS_DO_RECIBO, htmlDoRecibo, type DadosDoRecibo } from "./recibo";

/**
 * O que dá pra imprimir de um pedido (ou de um serviço dele): etiquetas únicas, de produção, de
 * entrega e recibo. Usado pelo "Imprimir ▾" dos dialogs e pelo "⋯" da lista de pedidos.
 */
export function useImpressaoDoPedido(order: IncomingOrderRow, servico?: ProsthesisServiceRow) {
  const utils = trpc.useUtils();
  const laboratorio = trpc.organization.getProfiles.useQuery();
  // Etiquetas do jeito que o laboratório configurou (Configurações › Etiquetas).
  const config = trpc.organization.etiquetas.useQuery().data ?? ETIQUETAS_PADRAO;
  const logo = laboratorio.data?.labProfile?.logoUrl ?? laboratorio.data?.logoUrl ?? null;
  const urlDoCatalogo = useLinkDoCatalogo();
  const podeVerPreco = usePermissao("prices.view");
  const entrega = entregaDoPedido(order);
  const temValor = order.services.some((s) => s.priceCents != null);
  const pedido = pedidoDaEtiqueta(order);
  const daProducao = (servico ? [servico] : order.services).map(paraEtiqueta);

  const producao = () => {
    const ok = imprimirPagina(`Etiquetas de produção — pedido ${codigoDoPedido(order)}`, paginaDeProducao(config.producao, pedido, daProducao, logo));
    if (!ok) toast.error(POPUP);
  };

  const lab = laboratorio.data;
  const remetente = { nome: lab?.name ?? "", cidade: lab?.mainAddress?.city, uf: lab?.mainAddress?.state, telefone: lab?.phone };

  const etiquetaDeEntrega = () => {
    if (!entrega) return;
    const pagina = paginaDeEntrega(config.entrega, pedido, order.services.map(paraEtiqueta), entrega, remetente, logo, urlDoCatalogo);
    const ok = imprimirPagina(`Etiqueta de entrega — pedido ${codigoDoPedido(order)}`, pagina);
    if (!ok) toast.error(POPUP);
  };

  // Uma por serviço (ou só a do serviço aberto), com a expedição embaixo de cada uma.
  const etiquetaUnica = () => {
    const ok = imprimirPagina(`Etiqueta única — pedido ${codigoDoPedido(order)}`, paginaUnicaDoPedido(config.unica, order, logo, servico, urlDoCatalogo));
    if (!ok) toast.error(POPUP);
  };

  const imprimirRecibo = async (forcar: boolean) => {
    if (!forcar && segurarDocumentoNaEstacao(() => void imprimirRecibo(true))) return;
    const janela = abrirImpressao(820, 1000);
    if (!janela) return toast.error(POPUP);
    try {
      const dados = (await utils.prosthesis.incoming.recibo.fetch({ id: order.id })) as DadosDoRecibo;
      janela.escrever(`Recibo — pedido ${codigoDoPedido(order)}`, CSS_DO_RECIBO, htmlDoRecibo(dados));
    } catch (e: any) {
      janela.fechar();
      toast.error(e?.message || "Não foi possível montar o recibo.");
    }
  };


  const recibo = () => void imprimirRecibo(false);

  return { producao, etiquetaDeEntrega, etiquetaUnica, recibo, entrega, temValor, podeVerPreco, quantidade: daProducao.length };
}
